/** שם לקוח להצעת מחיר — URL, localStorage, customer_quote_display (כולל שם פרטי/משפחה ל־CRM). */

const STORAGE_PREFIX = "hr_pkg_proposal_cn:";
const IDENTITY_STORAGE_PREFIX = "hr_pkg_proposal_id:";

const NAME_PART_MAX = 60;

export const PROPOSAL_CUSTOMER_NAME_QUERY_KEYS = ["customerName", "cn"];
export const PROPOSAL_FIRST_NAME_QUERY_KEYS = ["firstName", "givenName", "fn"];
export const PROPOSAL_LAST_NAME_QUERY_KEYS = ["lastName", "familyName", "sn", "ln"];

export function normalizeProposalCustomerName(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  return s.slice(0, 120);
}

export function normalizeProposalNamePart(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  return s.slice(0, NAME_PART_MAX);
}

/** פנייה לתצוגה — "שם פרטי שם משפחה" */
export function formatProposalGreetingFromParts(firstName, lastName) {
  const f = normalizeProposalNamePart(firstName);
  const l = normalizeProposalNamePart(lastName);
  return normalizeProposalCustomerName([f, l].filter(Boolean).join(" "));
}

export function proposalCustomerNameStorageKey(orderId) {
  return `${STORAGE_PREFIX}${String(orderId).trim()}`;
}

export function proposalCustomerIdentityStorageKey(orderId) {
  return `${IDENTITY_STORAGE_PREFIX}${String(orderId).trim()}`;
}

export function readPersistedProposalCustomerName(orderId) {
  try {
    if (orderId == null || orderId === "" || typeof window === "undefined") return "";
    const v = window.localStorage.getItem(proposalCustomerNameStorageKey(orderId));
    return normalizeProposalCustomerName(v);
  } catch {
    return "";
  }
}

export function writePersistedProposalCustomerName(orderId, name) {
  try {
    if (orderId == null || orderId === "" || typeof window === "undefined") return;
    const n = normalizeProposalCustomerName(name);
    if (n) window.localStorage.setItem(proposalCustomerNameStorageKey(orderId), n);
  } catch {
    /* ignore */
  }
}

export function readPersistedProposalCustomerIdentity(orderId) {
  try {
    if (orderId == null || orderId === "" || typeof window === "undefined") {
      return { firstName: "", lastName: "" };
    }
    const raw = window.localStorage.getItem(proposalCustomerIdentityStorageKey(orderId));
    if (!raw) return { firstName: "", lastName: "" };
    const j = JSON.parse(raw);
    return {
      firstName: normalizeProposalNamePart(j.fn ?? j.firstName),
      lastName: normalizeProposalNamePart(j.ln ?? j.lastName),
    };
  } catch {
    return { firstName: "", lastName: "" };
  }
}

export function writePersistedProposalCustomerIdentity(orderId, { firstName, lastName }) {
  try {
    if (orderId == null || orderId === "" || typeof window === "undefined") return;
    const fn = normalizeProposalNamePart(firstName);
    const ln = normalizeProposalNamePart(lastName);
    if (!fn && !ln) return;
    window.localStorage.setItem(
      proposalCustomerIdentityStorageKey(orderId),
      JSON.stringify({ fn, ln })
    );
    const g = formatProposalGreetingFromParts(fn, ln);
    if (g) writePersistedProposalCustomerName(orderId, g);
  } catch {
    /* ignore */
  }
}

function readFromSearchParamKeys(searchParams, keys) {
  if (!searchParams) return "";
  for (const key of keys) {
    const v = searchParams.get(key);
    const n = normalizeProposalNamePart(v);
    if (n) return n;
  }
  return "";
}

export function readProposalCustomerNameFromSearchParams(searchParams) {
  if (!searchParams) return "";
  for (const key of PROPOSAL_CUSTOMER_NAME_QUERY_KEYS) {
    const v = searchParams.get(key);
    const n = normalizeProposalCustomerName(v);
    if (n) return n;
  }
  return "";
}

export function readProposalFirstNameFromSearchParams(searchParams) {
  return readFromSearchParamKeys(searchParams, PROPOSAL_FIRST_NAME_QUERY_KEYS);
}

export function readProposalLastNameFromSearchParams(searchParams) {
  return readFromSearchParamKeys(searchParams, PROPOSAL_LAST_NAME_QUERY_KEYS);
}

/**
 * סדר עדיפות לשדות: session (סטייט ריאקט) → URL → הזמנה מהשרת → זהות ב-localStorage.
 * שם פנייה: customerName ב־URL → צמד שמות → greeting מההזמנה → מחרוזת legacy ב־LS.
 */
export function resolveProposalCustomerIdentityForOffer({
  orderId,
  searchParams,
  order,
  sessionFirstName = "",
  sessionLastName = "",
}) {
  const sf = normalizeProposalNamePart(sessionFirstName);
  const sl = normalizeProposalNamePart(sessionLastName);

  const urlFn = readProposalFirstNameFromSearchParams(searchParams);
  const urlLn = readProposalLastNameFromSearchParams(searchParams);
  const urlGreeting = readProposalCustomerNameFromSearchParams(searchParams);

  const orderFn = normalizeProposalNamePart(
    order?.customer_quote_display?.customer_first_name ??
      order?.customerQuoteDisplay?.customerFirstName
  );
  const orderLn = normalizeProposalNamePart(
    order?.customer_quote_display?.customer_last_name ??
      order?.customerQuoteDisplay?.customerLastName
  );
  const orderGreeting = normalizeProposalCustomerName(
    order?.customer_quote_display?.customer_greeting_name ??
      order?.customerQuoteDisplay?.customerGreetingName
  );

  const ls =
    orderId != null && orderId !== ""
      ? readPersistedProposalCustomerIdentity(orderId)
      : { firstName: "", lastName: "" };
  const lsLegacy =
    orderId != null && orderId !== "" ? readPersistedProposalCustomerName(orderId) : "";

  const firstName = sf || urlFn || orderFn || ls.firstName || "";
  const lastName = sl || urlLn || orderLn || ls.lastName || "";

  const greetingFromParts = formatProposalGreetingFromParts(firstName, lastName);
  const greetingName =
    normalizeProposalCustomerName(urlGreeting) ||
    (firstName && lastName ? greetingFromParts : "") ||
    orderGreeting ||
    normalizeProposalCustomerName(lsLegacy) ||
    greetingFromParts ||
    "";

  return { firstName, lastName, greetingName };
}

/** תאימות לאחור — מחזיר רק מחרוזת פנייה לתצוגה */
export function resolveProposalCustomerNameForOffer(args) {
  return resolveProposalCustomerIdentityForOffer(args).greetingName;
}

export function hasProposalCustomerCrmIdentity(identity) {
  if (!identity || typeof identity !== "object") return false;
  return !!(
    normalizeProposalNamePart(identity.firstName) &&
    normalizeProposalNamePart(identity.lastName)
  );
}

/**
 * זהות לקוח להצעת מחיר תחת ‎/searchoffer (מלון / דינמי) — עדיפות לערכים מהמודל, אחרת URL + LS לפי orderId.
 */
export function resolveHotelSearchOfferProposalIdentity({
  hotelOrderId,
  dynamicOrderId,
  searchParams,
  proposalNameOverride,
}) {
  const oid =
    hotelOrderId != null && String(hotelOrderId).trim() !== ""
      ? hotelOrderId
      : dynamicOrderId != null && String(dynamicOrderId).trim() !== ""
        ? dynamicOrderId
        : null;
  const ofn = normalizeProposalNamePart(proposalNameOverride?.firstName);
  const oln = normalizeProposalNamePart(proposalNameOverride?.lastName);
  if (ofn && oln) {
    return {
      firstName: ofn,
      lastName: oln,
      greetingName: formatProposalGreetingFromParts(ofn, oln),
    };
  }
  return resolveProposalCustomerIdentityForOffer({
    orderId: oid,
    searchParams,
    order: null,
    sessionFirstName: "",
    sessionLastName: "",
  });
}
